// TES-1019 (Emelie, point 7) — the rules of the care meeting's Webdoc step,
// the modal after "Dokumentera och avsluta mötet":
//   "Före publicering måste Kirill bekräfta: ”Journalanteckning och ordination är införda i Webdoc”."
//   "Om han har valt Nytt blodprov som komplettering visas dessutom: ”Provbeställning/remiss är klar i Webdoc”."
//   "Först därefter aktiveras Bekräfta och publicera till patienten."
// TES-1181 (TES-1180 point 5) — A-02 2026-10-08 renamed both ticks:
// "Journalanteckningen är införd i Webdoc" and
// "Provbeställning/remiss för blodprov är skapad i Webdoc".
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.
import type { WebdocControl } from "@care-by-ella/contracts";
import { requisitionRefControl } from "./requisition-ref";

export interface WebdocStepState {
  /** ☐ "Journalanteckningen är införd i Webdoc" */
  journalDone: boolean;
  /** Whether the completion picker asked for a new blood test. */
  needsLabOrder: boolean;
  /** ☐ "Provbeställning/remiss för blodprov är skapad i Webdoc" */
  labOrderDone: boolean;
  /** TES-442 — "Remissnummer i Webdoc (valfritt)", as typed. */
  requisitionRef?: string;
}

/**
 * The remiss row shows only when the doctor chose "Nytt blodprov" in the
 * completion picker; it confirms the order is placed, not the need.
 */
export function meetingNeedsLabOrder(completion: {
  outcome: string | null;
  items: readonly string[];
}): boolean {
  return (
    completion.outcome === "completion" &&
    completion.items.includes("new_blood_test")
  );
}

/** "Bekräfta och publicera till patienten" is enabled only when this holds. */
export function canPublishMeeting(s: WebdocStepState): boolean {
  return s.journalDone && (!s.needsLabOrder || s.labOrderDone);
}

/** The `webdocControl` the publish sends (documentMeetingSchema). */
export function meetingWebdocControl(s: WebdocStepState): WebdocControl {
  return {
    journalInWebdoc: s.journalDone,
    ...(s.needsLabOrder ? { labOrderDoneInWebdoc: s.labOrderDone } : {}),
    ...requisitionRefControl(s),
  };
}

/**
 * TES-1181 (TES-1180 point 5) — a request that publishes no journal note
 * (the overview's "Begär komplettering", "Hantera provsvar från Webdoc")
 * carries only the remiss tick, and only when a new blood test is asked for.
 */
export function labOrderOnlyControl(s: {
  needsLabOrder: boolean;
  labOrderDone: boolean;
  requisitionRef?: string;
}): WebdocControl | undefined {
  if (!s.needsLabOrder) return undefined;
  return {
    labOrderDoneInWebdoc: s.labOrderDone,
    ...requisitionRefControl(s),
  };
}

/**
 * TES-1315 (A-02 2026-10-13, STEG 4) — a decision that offers only a new
 * e-recept shows it as its own tick rather than a choice: every new
 * approval, the direct approval at the first meeting included.
 */
export function isERecept(choices: readonly string[]): boolean {
  return choices.length === 1 && choices[0] === "new_issued";
}

/**
 * TES-1315 (Simon 2026-10-13) — the note's "Receptstatus" follows the
 * e-recept tick, so on an approval that tick comes first and the journal
 * tick waits for it: the note is final before it is copied and confirmed.
 */
export function journalWaitsForERecept(s: {
  eRecept: boolean;
  eReceptDone: boolean;
}): boolean {
  return s.eRecept && !s.eReceptDone;
}

/** The hint beside the journal tick while it waits for the e-recept. */
export const JOURNAL_WAITS_FOR_E_RECEPT_HINT =
  "Bekräfta e-receptet först. Receptstatusen skrivs in i journalanteckningen.";
